import Grid from '../components/grid'
import Container from '../components/ui/container'
import Text from '../components/ui/text'
import { Asset } from '../v2/components/Asset'
import { CMSLink } from '../v2/components/CMSLink'
import Divider from './Divider'
import { Page, Asset as AssetType } from '@telesoft/types/payload-types'
import cn from 'classnames'
import React, { ReactElement } from 'react'
import { RichText } from '../v2/components/RichText'

export type BlockProps = Extract<Page['blocks'][0], { blockType: 'content-image' }>

const getThemeClass = (theme: string): string => (theme === 'light' ? 'text-white' : 'text-black')
const getTextAlignmentClass = (alignment: string): string =>
  alignment === 'text-center' ? 'text-center items-center' : 'text-left items-start'

interface TitleProps {
  isSubtitle: boolean | undefined
  theme: string | undefined
  subTitle: string | undefined
  title: string
  titleColour: string | undefined
}

const Title: React.FC<TitleProps> = ({ isSubtitle, theme, subTitle, title, titleColour }) => (
  <div className="">
    {isSubtitle && (
      <h4 className={cn('mb-3', getThemeClass(theme ? theme : 'dark'))}>{subTitle}</h4>
    )}
    <Text
      variant="subHeading"
      className={cn(
        'pb-4 md:pb-6 lg:pb-8 w-full',
        titleColour === 'blue' ? 'text-telesoft' : 'text-black',
      )}
    >
      {title}
    </Text>
  </div>
)

const ContentImage: React.FC<BlockProps> = (block: BlockProps): ReactElement => {
  const { title, description, image, options } = block
  const {
    isSubtitle,
    isButton,
    link,
    textAlignment,
    theme,
    subTitle,
    titleColour,
    overlayOpacity,
    lines = true,
  } = options

  const themeClass = getThemeClass(theme as string)
  const textAlignmentClass = getTextAlignmentClass(textAlignment as string)

  return (
    <>
      <Divider withLine={lines} />
      <Container>
        <Grid>
          <div className="col-span-full relative min-h-[442px] p-8 md:p-12 lg:p-20 bg-accent-1 flex flex-col justify-center items-start">
            <div className="absolute inset-0 z-10 mix-blend-soft-light bg-gradient-to-b from-telesoft to-transparent" />
            <div
              style={{ opacity: `${overlayOpacity}%` }}
              className={cn(`absolute inset-0 z-10`, {
                'bg-white': theme === 'dark',
                'bg-black': theme === 'light',
              })}
            />
            <div className={cn(`z-10 flex flex-col justify-center my-10`, textAlignmentClass)}>
              <Title {...{ isSubtitle, theme, subTitle, title, titleColour }} />
              <RichText content={description} className={cn('text-white max-w-4xl', themeClass)} />
              {isButton && (
                <CMSLink
                  {...link}
                  className={cn('md:mt-7 lg:mt-8', themeClass)}
                />
              )}
            </div>
            <div className="absolute inset-0">
              <Asset resource={image as AssetType} imgClassName="object-cover object-center" fill />
            </div>
          </div>
        </Grid>
      </Container>
      <Divider withLine={lines} />
    </>
  )
}

export default ContentImage
